/**
 * FinancialStatisticsPage — iter-238 (Feb 2026)
 *
 * User asks:
 *   1. Remove the monthly-bars/dates block at the bottom of Salaries +
 *      Company Payments cards and shrink them.
 *   2. Wrap Salaries + Company Payments in a single parent card called
 *      "إحصائيات الشركة" (Company Statistics).
 *   3. Fix broken navigation: clicking Salaries card should open the
 *      Salaries records ledger; Company Payments card should open the
 *      Company Payments records ledger.
 *   4. Add a second parent card "إحصائيات منصة التجار" with 4 KPIs
 *      (Total Revenue, Total Gov Fees, Total Commission Sent, Net Profit)
 *      from GET /api/admin/company-registrations/revenue.
 *   5. Add a third parent card "إحصائيات منصة المتعاملين" as a placeholder
 *      (Coming Soon) with the same 4 KPI slots.
 *
 * Route: /dashboard/finance/statistics
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart3, Wallet, Building2, ArrowUpRight, Loader2,
  RefreshCw, Store, Users as UsersIcon, Landmark, HandCoins, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import DirhamPrice from '../../components/DirhamPrice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Compact stat card — no monthly bars, no bar-chart footer. */
const CompactStatCard = ({
  icon: Icon, titleAr, titleEn, valueAed, entriesCount, tone, onClick, testId, isAr, comingSoon,
}) => {
  const t = (ar, en) => (isAr ? ar : en);
  return (
    <motion.button
      whileHover={comingSoon ? undefined : { y: -3 }}
      whileTap={comingSoon ? undefined : { scale: 0.99 }}
      onClick={comingSoon ? undefined : onClick}
      disabled={!!comingSoon}
      className={`text-start relative overflow-hidden rounded-2xl p-4 border transition-colors
                  bg-white/[0.02] ${tone.border} ${comingSoon ? 'opacity-60 cursor-not-allowed' : tone.hoverBorder}`}
      data-testid={testId}
    >
      <div className={`absolute -top-14 -end-14 w-40 h-40 rounded-full blur-3xl ${tone.glow}`} />
      <div className="relative">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tone.iconBg} ${tone.iconBorder}`}>
            <Icon className={`w-5 h-5 ${tone.iconColor}`} />
          </div>
          {comingSoon ? (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/50 border border-white/10">
              {t('قريباً', 'Soon')}
            </span>
          ) : (
            <ArrowUpRight className={`w-4 h-4 text-white/30 ${isAr ? 'rotate-[-90deg]' : ''}`} />
          )}
        </div>
        <h3 className="text-xs text-white/60 font-medium mb-1">
          {t(titleAr, titleEn)}
        </h3>
        <div className={`text-xl font-bold ${tone.valueColor}`}>
          {comingSoon
            ? <span className="text-white/40 text-sm">—</span>
            : <DirhamPrice value={valueAed || 0} decimals={2} size="md" />}
        </div>
        {typeof entriesCount === 'number' && !comingSoon && (
          <div className="mt-1 text-[10px] text-white/40">
            {t(`${entriesCount} إدخال`, `${entriesCount} entries`)}
          </div>
        )}
      </div>
    </motion.button>
  );
};

/** Parent card that groups multiple stat cards under a single title. */
const StatGroup = ({ icon: Icon, titleAr, titleEn, subtitleAr, subtitleEn, isAr, accent, children, testId }) => {
  const t = (ar, en) => (isAr ? ar : en);
  return (
    <section
      className="rounded-2xl border p-5 bg-white/[0.015] backdrop-blur-sm"
      style={{ borderColor: `${accent}30` }}
      data-testid={testId}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center border"
          style={{ backgroundColor: `${accent}15`, borderColor: `${accent}40`, color: accent }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-base font-bold text-white">{t(titleAr, titleEn)}</h2>
          {(subtitleAr || subtitleEn) && (
            <p className="text-[11px] text-white/50">{t(subtitleAr, subtitleEn)}</p>
          )}
        </div>
      </div>
      {children}
    </section>
  );
};

export default function FinancialStatisticsPage() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const { token } = useAuth();

  // iter-286 (Feb 2026) — Switch Button between the 3 stat groups.
  // Default: 'company' per user spec.
  const [activeTab, setActiveTab] = useState('company'); // 'company' | 'merchant' | 'customer'

  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);          // merchant-platform revenue totals
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const authHeaders = { Authorization: `Bearer ${token}` };
      // Fetch both endpoints in parallel — company-registrations/revenue is
      // the source for "إحصائيات منصة التجار" (agent system revenue).
      const [statsRes, revRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/finance/stats/summary`, { headers: authHeaders }),
        axios.get(`${BACKEND_URL}/api/admin/company-registrations/revenue`, { headers: authHeaders }),
      ]);
      setStats(statsRes.data);
      setRevenue(revRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('فشل تحميل الإحصائيات', 'Failed to load stats'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // -------- tone presets --------
  const salaryTone = {
    border: 'border-blue-500/25', hoverBorder: 'hover:border-blue-500/50',
    glow: 'bg-blue-500/5',
    iconBg: 'bg-blue-500/15', iconBorder: 'border border-blue-500/40',
    iconColor: 'text-blue-300', valueColor: 'text-blue-100',
  };
  const paymentsTone = {
    border: 'border-amber-500/25', hoverBorder: 'hover:border-amber-500/50',
    glow: 'bg-amber-500/5',
    iconBg: 'bg-amber-500/15', iconBorder: 'border border-amber-500/40',
    iconColor: 'text-amber-300', valueColor: 'text-amber-100',
  };
  const revenueTone = {
    border: 'border-emerald-500/25', hoverBorder: 'hover:border-emerald-500/50',
    glow: 'bg-emerald-500/5',
    iconBg: 'bg-emerald-500/15', iconBorder: 'border border-emerald-500/40',
    iconColor: 'text-emerald-300', valueColor: 'text-emerald-100',
  };
  const govTone = {
    border: 'border-cyan-500/25', hoverBorder: 'hover:border-cyan-500/50',
    glow: 'bg-cyan-500/5',
    iconBg: 'bg-cyan-500/15', iconBorder: 'border border-cyan-500/40',
    iconColor: 'text-cyan-300', valueColor: 'text-cyan-100',
  };
  const commissionTone = {
    border: 'border-purple-500/25', hoverBorder: 'hover:border-purple-500/50',
    glow: 'bg-purple-500/5',
    iconBg: 'bg-purple-500/15', iconBorder: 'border border-purple-500/40',
    iconColor: 'text-purple-300', valueColor: 'text-purple-100',
  };
  const netTone = {
    border: 'border-yellow-500/25', hoverBorder: 'hover:border-yellow-500/50',
    glow: 'bg-yellow-500/5',
    iconBg: 'bg-yellow-500/15', iconBorder: 'border border-yellow-500/40',
    iconColor: 'text-yellow-300', valueColor: 'text-yellow-100',
  };

  return (
    <div className="min-h-screen bg-[#030303] relative">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#8B5CF6]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/finance" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
            { label_ar: 'الإحصائيات المالية', label_en: 'Financial Statistics', path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-[#8B5CF6]" />
              {t('الإحصائيات المالية', 'Financial Statistics')}
            </h1>
            <p className="text-white/50 text-sm">
              {t('كل الأرقام المالية للشركة والمنصات في مكان واحد',
                 'All company & platform financials — at a glance')}
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 text-xs font-medium disabled:opacity-50"
            data-testid="stats-refresh-btn"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {t('تحديث', 'Refresh')}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-32 text-white/50 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('جاري التحميل…', 'Loading…')}
          </div>
        ) : (
          <div className="space-y-6">
            {/* iter-286 — Three-way Switch Button */}
            <div
              role="tablist"
              className="inline-flex rounded-xl bg-white/[0.03] border border-white/10 p-1 gap-1"
              data-testid="stats-tab-switch"
            >
              {[
                { key: 'company',  ar: 'الشركة',            en: 'Company',            Icon: Building2, accent: '#d4af37' },
                { key: 'merchant', ar: 'منصة التجار',       en: 'Merchant Platform',  Icon: Store,     accent: '#10b981' },
                { key: 'customer', ar: 'منصة المتعاملين',   en: 'Customer Platform',  Icon: UsersIcon, accent: '#3b82f6' },
              ].map((tab) => {
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    role="tab"
                    aria-selected={active}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold transition-all ${
                      active
                        ? 'bg-white/10 text-white'
                        : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                    style={active ? { boxShadow: `inset 0 0 0 1px ${tab.accent}66` } : undefined}
                    data-testid={`stats-tab-${tab.key}`}
                  >
                    <tab.Icon className="w-4 h-4" style={active ? { color: tab.accent } : undefined} />
                    {t(tab.ar, tab.en)}
                  </button>
                );
              })}
            </div>

            {/* 1) إحصائيات الشركة — Salaries + Company Payments */}
            {activeTab === 'company' && (
            <StatGroup
              icon={Building2}
              accent="#d4af37"
              titleAr="إحصائيات الشركة"
              titleEn="Company Statistics"
              subtitleAr="رواتب الموظفين + مدفوعات الشركة (اشتراكات وخدمات)"
              subtitleEn="Employee salaries + company payments (subscriptions & services)"
              isAr={isAr}
              testId="stats-company-group"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <CompactStatCard
                  icon={Wallet}
                  titleAr="رواتب الموظفين"
                  titleEn="Employee Salaries"
                  valueAed={stats?.salaries?.total_aed || 0}
                  entriesCount={stats?.salaries?.entries_count || 0}
                  tone={salaryTone}
                  onClick={() => navigate('/finance/asb606/payments/salaries?mode=records')}
                  testId="stats-salaries-card"
                  isAr={isAr}
                />
                <CompactStatCard
                  icon={Building2}
                  titleAr="مدفوعات الشركة"
                  titleEn="Company Payments"
                  valueAed={stats?.company_payments?.total_aed || 0}
                  entriesCount={stats?.company_payments?.entries_count || 0}
                  tone={paymentsTone}
                  onClick={() => navigate('/finance/asb606/payments/company?mode=records')}
                  testId="stats-company-payments-card"
                  isAr={isAr}
                />
              </div>

              {/* Non-AED salary warning kept as it was — but subtle */}
              {(stats?.salaries?.total_non_aed || 0) > 0 && (
                <div className="mt-3 rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-2.5 text-[11px] text-amber-200">
                  {t(
                    `تنبيه: هناك ${stats.salaries.total_non_aed.toLocaleString()} في عملات غير درهم (${(stats.salaries.non_aed_currencies || []).join(', ')}) — لم تُدمج في المجموع.`,
                    `Note: ${stats.salaries.total_non_aed.toLocaleString()} in non-AED currencies (${(stats.salaries.non_aed_currencies || []).join(', ')}) — not included.`,
                  )}
                </div>
              )}
            </StatGroup>
            )}

            {/* 2) إحصائيات منصة التجار — 5 KPIs per iter-286 spec */}
            {activeTab === 'merchant' && (
            <StatGroup
              icon={Store}
              accent="#10b981"
              titleAr="إحصائيات منصة التجار"
              titleEn="Merchant Platform Statistics"
              subtitleAr="رصيد ومدفوعات العمولة والإيرادات والمبالغ الحكومية"
              subtitleEn="Commission balance, payouts, revenues and government payments"
              isAr={isAr}
              testId="stats-merchant-group"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <CompactStatCard
                  icon={Wallet}
                  titleAr="مبلغ الكوميشن المتوفر"
                  titleEn="Available Commission Balance"
                  valueAed={revenue?.commission_available_aed || 0}
                  tone={revenueTone}
                  onClick={() => navigate('/dashboard/finance/cloudgm/merchants/tax-invoices')}
                  testId="stats-merchant-commission-available"
                  isAr={isAr}
                />
                <CompactStatCard
                  icon={HandCoins}
                  titleAr="مبلغ الكوميشن المرسل"
                  titleEn="Commission Sent"
                  valueAed={revenue?.total_agent_commission_aed || 0}
                  tone={commissionTone}
                  onClick={() => navigate('/dashboard/finance/cloudgm/merchants/withdrawal-requests?status=approved')}
                  testId="stats-merchant-commission-sent"
                  isAr={isAr}
                />
                <CompactStatCard
                  icon={ArrowUpRight}
                  titleAr="مبالغ مستحقة لمنصة التجار"
                  titleEn="Amounts Owed to Merchant Platform"
                  valueAed={revenue?.commission_owed_aed || 0}
                  tone={paymentsTone}
                  onClick={() => navigate('/dashboard/finance/cloudgm/merchants/withdrawal-requests?status=pending')}
                  testId="stats-merchant-owed"
                  isAr={isAr}
                />
                <CompactStatCard
                  icon={TrendingUp}
                  titleAr="مجموع إيرادات المنصة"
                  titleEn="Total Platform Revenue"
                  valueAed={revenue?.total_aed || 0}
                  tone={netTone}
                  onClick={() => navigate('/dashboard/finance/cloudgm/merchants/tax-invoices')}
                  testId="stats-merchant-total-revenue"
                  isAr={isAr}
                />
                <CompactStatCard
                  icon={Landmark}
                  titleAr="مبالغ مدفوعة لجهات حكومية"
                  titleEn="Paid to Government Entities"
                  valueAed={revenue?.total_gov_fees_aed || 0}
                  tone={govTone}
                  onClick={() => navigate('/dashboard/finance/cloudgm/merchants/tax-invoices')}
                  testId="stats-merchant-gov-paid"
                  isAr={isAr}
                />
              </div>
            </StatGroup>
            )}

            {/* 3) إحصائيات منصة المتعاملين — placeholder, same layout, coming soon */}
            {activeTab === 'customer' && (
            <StatGroup
              icon={UsersIcon}
              accent="#3b82f6"
              titleAr="إحصائيات منصة المتعاملين"
              titleEn="Customer Platform Statistics"
              subtitleAr="سيتم تعبئة الأرقام قريباً بعد ربط المصدر"
              subtitleEn="Numbers coming soon once the data source is wired"
              isAr={isAr}
              testId="stats-customer-group"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <CompactStatCard icon={TrendingUp} titleAr="إجمالي الإيرادات" titleEn="Total Revenue"
                                 tone={revenueTone} isAr={isAr} comingSoon
                                 testId="stats-customer-total-revenue" />
                <CompactStatCard icon={Landmark} titleAr="إجمالي الرسوم الحكومية المدفوعة" titleEn="Total Gov Fees Paid"
                                 tone={govTone} isAr={isAr} comingSoon
                                 testId="stats-customer-total-gov" />
                <CompactStatCard icon={HandCoins} titleAr="إجمالي العمولة المرسلة" titleEn="Total Commission Sent"
                                 tone={commissionTone} isAr={isAr} comingSoon
                                 testId="stats-customer-total-commission" />
                <CompactStatCard icon={BarChart3} titleAr="صافي الربح" titleEn="Net Profit"
                                 tone={netTone} isAr={isAr} comingSoon
                                 testId="stats-customer-net-profit" />
              </div>
            </StatGroup>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
